import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Select, Input, DatePicker, Drawer,
  Descriptions, Timeline, Dropdown, message, Popconfirm, Badge, Spin, Empty
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, EyeOutlined,
  SendOutlined, QrcodeOutlined, SyncOutlined, TeamOutlined,
  RightOutlined, MoreOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const STATUS_MAP = {
  pending: { color: 'default', label: '待处理' },
  submitted: { color: 'processing', label: '已提交' },
  completed: { color: 'success', label: '已完成' },
  failed: { color: 'error', label: '失败' },
  cancelled: { color: 'default', label: '已取消' }
};

function ApiOrderList() {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [userList, setUserList] = useState([]);
  const [filter, setFilter] = useState({
    platform: '',
    strategy_id: '',
    status: '',
    keyword: '',
    user_id: '',
    dateRange: null
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const isSuperAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.username === 'admin';
    } catch { return false; }
  })();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.platform) params.platform = filter.platform;
      if (filter.strategy_id) params.strategy_id = filter.strategy_id;
      if (filter.status) params.status = filter.status;
      if (filter.keyword) params.keyword = filter.keyword;
      if (filter.user_id && isSuperAdmin) params.user_id = filter.user_id;
      if (filter.dateRange && filter.dateRange[0]) {
        params.date_start = filter.dateRange[0].format('YYYY-MM-DD');
        params.date_end = filter.dateRange[1].format('YYYY-MM-DD');
      }

      const res = await api.get('/api-orders', { params });
      if (res.code === 200) setList(res.data || []);
    } catch (err) {
      message.error('加载对接列表失败');
    } finally {
      setLoading(false);
    }
  }, [filter, isSuperAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // 加载筛选选项
  useEffect(() => {
    api.get('/api-strategies').then(res => {
      if (res.code === 200) setStrategies(res.data || []);
    }).catch(() => {});
    api.get('/platforms').then(res => {
      if (res.code === 200) setPlatforms(res.data || []);
    }).catch(() => {});
    if (isSuperAdmin) {
      api.get('/api-orders/meta/users').then(res => {
        if (res.code === 200) setUserList(res.data || []);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const loadDetail = async (id) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await api.get(`/api-orders/${id}`);
      if (res.code === 200) setDetailData(res.data);
    } catch (err) {
      message.error('加载详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAdvanceStep = async (id) => {
    try {
      const res = await api.post(`/api-orders/${id}/advance-step`);
      if (res.code === 200) {
        message.success(res.message);
        loadData();
        if (detailOpen && detailData?.id === id) loadDetail(id);
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleExecuteApi = async (orderId, apiId) => {
    try {
      const res = await api.post(`/api-orders/${orderId}/execute-api/${apiId}`);
      if (res.code === 200) {
        message.success(res.message);
        loadData();
        if (detailOpen && detailData?.id === orderId) loadDetail(orderId);
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  const resetFilter = () => {
    setFilter({
      platform: '',
      strategy_id: '',
      status: '',
      keyword: '',
      user_id: '',
      dateRange: null
    });
  };

  // 构建操作下拉菜单
  const getActionItems = (record) => {
    const items = [
      { key: 'detail', label: '查看详情', icon: <EyeOutlined /> },
      { type: 'divider' }
    ];

    // 从策略加载可用的API操作
    const strategy = strategies.find(s => s.id === record.strategy_id);
    const apis = strategy?.apis || [];

    // 发送授权
    const sendAuth = apis.find(a => a.operation_type === 'send_auth');
    if (sendAuth) {
      items.push({
        key: 'send_auth',
        label: '发送授权',
        icon: <SendOutlined />,
        onClick: () => handleExecuteApi(record.id, sendAuth.id)
      });
    }

    // 获取授权码
    const getAuthCode = apis.find(a => a.operation_type === 'get_auth_code');
    if (getAuthCode) {
      items.push({
        key: 'get_auth_code',
        label: '获取授权码',
        icon: <QrcodeOutlined />,
        onClick: () => handleExecuteApi(record.id, getAuthCode.id)
      });
    }

    // 查询状态
    const queryStatus = apis.find(a => a.operation_type === 'query_status');
    if (queryStatus) {
      items.push({
        key: 'query_status',
        label: '查询状态',
        icon: <SyncOutlined />,
        onClick: () => handleExecuteApi(record.id, queryStatus.id)
      });
    }

    // 查询粉丝
    const queryFans = apis.find(a => a.operation_type === 'query_fans');
    if (queryFans) {
      items.push({
        key: 'query_fans',
        label: '查询粉丝',
        icon: <TeamOutlined />,
        onClick: () => handleExecuteApi(record.id, queryFans.id)
      });
    }

    // 推进步骤
    const workflowSteps = strategy?.workflow_steps || [];
    if (record.current_step < workflowSteps.length - 1) {
      items.push({ type: 'divider' });
      items.push({
        key: 'advance',
        label: `推进到下一步 (${workflowSteps[record.current_step + 1] || ''})`,
        icon: <RightOutlined />,
        onClick: () => handleAdvanceStep(record.id)
      });
    }

    return items;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '下单人', dataIndex: 'user_name', width: 100 },
    { title: '账号', dataIndex: 'account_name', width: 140 },
    { title: '策略', dataIndex: 'strategy_name', width: 140 },
    { title: '平台', dataIndex: 'platform', width: 80 },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: v => {
        const s = STATUS_MAP[v] || { color: 'default', label: v };
        return <Tag color={s.color}>{s.label}</Tag>;
      }
    },
    {
      title: '进度/步骤', width: 160,
      render: (_, record) => {
        const strategy = strategies.find(s => s.id === record.strategy_id);
        const steps = strategy?.workflow_steps || [];
        if (record.status === 'completed') return <Tag color="green">✓ 已完成</Tag>;
        const stepText = record.progress || (steps[record.current_step] || `步骤${record.current_step}`);
        return (
          <span>
            <Badge status={record.status === 'failed' ? 'error' : 'processing'} />
            {record.current_step + 1}. {stepText}
          </span>
        );
      }
    },
    ...(isSuperAdmin ? [
      { title: '供应商', dataIndex: 'supplier_name', width: 120 },
      { title: '第三方订单号', dataIndex: 'external_order_id', width: 140, render: v => v || '-' }
    ] : []),
    {
      title: '初始粉', dataIndex: 'initial_fans', width: 80,
      render: v => v != null ? v.toLocaleString() : '-'
    },
    {
      title: '最终粉', dataIndex: 'final_fans', width: 80,
      render: v => v != null ? v.toLocaleString() : '-'
    },
    {
      title: '涨粉', width: 80,
      render: (_, record) => {
        if (record.fans_gained != null) return record.fans_gained.toLocaleString();
        if (record.initial_fans != null && record.final_fans != null) {
          return (record.final_fans - record.initial_fans).toLocaleString();
        }
        return '-';
      }
    },
    {
      title: '提交时间', dataIndex: 'created_at', width: 150,
      render: v => v ? dayjs(v).format('MM-DD HH:mm') : '-'
    },
    {
      title: '操作', width: 80, fixed: 'right',
      render: (_, record) => (
        <Dropdown menu={{ items: getActionItems(record) }} trigger={['click']}>
          <Button type="link" icon={<MoreOutlined />}>操作</Button>
        </Dropdown>
      )
    }
  ];

  // 详情抽屉
  const renderDetail = () => {
    if (!detailData) return null;

    const strategy = strategies.find(s => s.id === detailData.strategy_id);
    const workflowSteps = strategy?.workflow_steps || detailData.workflow_steps || [];

    return (
      <Drawer
        title="订单详情"
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailData(null); }}
        width={640}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="账号">{detailData.account_name}</Descriptions.Item>
              <Descriptions.Item label="平台">{detailData.platform}</Descriptions.Item>
              <Descriptions.Item label="策略">{detailData.strategy_name}</Descriptions.Item>
              {isSuperAdmin && (
                <Descriptions.Item label="供应商">{detailData.supplier_name || '-'}</Descriptions.Item>
              )}
              {isSuperAdmin && (
                <Descriptions.Item label="第三方订单号">{detailData.external_order_id || '-'}</Descriptions.Item>
              )}
              <Descriptions.Item label="状态">
                <Tag color={STATUS_MAP[detailData.status]?.color}>{STATUS_MAP[detailData.status]?.label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="当前步骤">
                {detailData.current_step + 1}. {detailData.progress || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="初始粉丝">{detailData.initial_fans?.toLocaleString() || '-'}</Descriptions.Item>
              <Descriptions.Item label="最终粉丝">{detailData.final_fans?.toLocaleString() || '-'}</Descriptions.Item>
              <Descriptions.Item label="涨粉">{detailData.fans_gained?.toLocaleString() || '-'}</Descriptions.Item>
              <Descriptions.Item label="下单人">{detailData.user_name}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{detailData.created_at ? dayjs(detailData.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            </Descriptions>

            {detailData.remark && (
              <div style={{ marginBottom: 16, padding: 8, background: '#f6f6f6', borderRadius: 4 }}>
                备注: {detailData.remark}
              </div>
            )}

            {/* 流程进度 */}
            {workflowSteps.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12 }}>流程进度</h4>
                <Timeline
                  items={workflowSteps.map((step, i) => {
                    let color = 'gray';
                    let dot = null;
                    if (i < detailData.current_step) { color = 'green'; }
                    else if (i === detailData.current_step) { color = 'blue'; dot = <Badge status="processing" />; }
                    return {
                      color,
                      dot,
                      children: (
                        <span style={{ color: i === detailData.current_step ? '#1677ff' : (i < detailData.current_step ? '#52c41a' : '#999') }}>
                          {i + 1}. {step}
                          {i === detailData.current_step && detailData.status !== 'completed' && (
                            <Tag color="processing" style={{ marginLeft: 8 }}>当前</Tag>
                          )}
                          {i < detailData.current_step && (
                            <Tag color="success" style={{ marginLeft: 8 }}>✓</Tag>
                          )}
                        </span>
                      )
                    };
                  })}
                />
              </div>
            )}

            {/* 执行日志 */}
            <div>
              <h4 style={{ marginBottom: 12 }}>执行日志</h4>
              {detailData.logs && detailData.logs.length > 0 ? (
                <Timeline
                  items={detailData.logs.map(log => ({
                    color: log.status === 'success' ? 'green' : 'red',
                    children: (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Space>
                            <Tag color={log.status === 'success' ? 'green' : 'red'}>
                              {log.operation_type}
                            </Tag>
                            <span>{log.action}</span>
                          </Space>
                          <span style={{ color: '#999', fontSize: 12 }}>
                            {log.created_at ? dayjs(log.created_at).format('MM-DD HH:mm:ss') : '-'}
                          </span>
                        </div>
                        {log.request_data && Object.keys(log.request_data).length > 0 && (
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            请求: <code>{JSON.stringify(log.request_data)}</code>
                          </div>
                        )}
                        {log.response_data && Object.keys(log.response_data).length > 0 && (
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            响应: <code>{JSON.stringify(log.response_data)}</code>
                          </div>
                        )}
                        {log.error_message && (
                          <div style={{ fontSize: 12, color: '#ff4d4f' }}>
                            错误: {log.error_message}
                          </div>
                        )}
                      </div>
                    )
                  }))}
                />
              ) : (
                <Empty description="暂无日志" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </>
        )}
      </Drawer>
    );
  };

  return (
    <div>
      <Card
        title="对接列表"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
          </Space>
        }
      >
        {/* 筛选栏 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Select
            value={filter.platform || undefined}
            onChange={v => setFilter(prev => ({ ...prev, platform: v || '' }))}
            allowClear
            placeholder="平台"
            style={{ width: 120 }}
            options={platforms.map(p => ({ value: p.name, label: p.name }))}
          />
          <Select
            value={filter.strategy_id || undefined}
            onChange={v => setFilter(prev => ({ ...prev, strategy_id: v || '' }))}
            allowClear
            placeholder="策略"
            style={{ width: 180 }}
            showSearch
            optionFilterProp="label"
            options={strategies.map(s => ({ value: s.id, label: s.name }))}
          />
          <Select
            value={filter.status || undefined}
            onChange={v => setFilter(prev => ({ ...prev, status: v || '' }))}
            allowClear
            placeholder="状态"
            style={{ width: 120 }}
            options={Object.entries(STATUS_MAP).map(([k, v]) => ({ value: k, label: v.label }))}
          />
          <Input
            value={filter.keyword}
            onChange={e => setFilter(prev => ({ ...prev, keyword: e.target.value }))}
            placeholder="搜索账号"
            style={{ width: 160 }}
            allowClear
          />
          {isSuperAdmin && (
            <Select
              value={filter.user_id || undefined}
              onChange={v => setFilter(prev => ({ ...prev, user_id: v || '' }))}
              allowClear
              placeholder="下单人"
              style={{ width: 120 }}
              options={userList.map(u => ({ value: u.user_id, label: u.user_name }))}
            />
          )}
          <RangePicker
            value={filter.dateRange}
            onChange={dates => setFilter(prev => ({ ...prev, dateRange: dates }))}
            placeholder={['开始日期', '结束日期']}
          />
          <Button icon={<SearchOutlined />} type="primary" onClick={loadData}>查询</Button>
          <Button onClick={resetFilter}>重置</Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `共 ${t} 条` }}
          size="middle"
        />
      </Card>

      {renderDetail()}
    </div>
  );
}

export default ApiOrderList;
